<template>
  <!--
    The nav BAR renders at every width — its labels are links, so the header
    must never vanish (client: it disappeared entirely on tablet portrait, where
    the legacy header stayed visible). The dropdown PANEL now also renders at
    every width using the same desktop styling; the panel's max-width:1440px +
    justify-content:center on the inner keeps the block centred at any viewport,
    and the promo pane's flex-shrink lets it absorb any shortfall below 1440.
  -->
  <div
    class="top-menu"
    :class="{ 'top-menu--overflowing': topMenuOverflowing }"
    @mouseleave="closeMenu"
  >
    <ul ref="topMenuNav" class="nav">
      <li
        v-for="item in items"
        :key="item.id"
        class="nav-item"
        @pointerenter="onPointerEnter($event, item)"
        @pointerdown="lastPointerType = $event.pointerType || 'mouse'"
        @mouseenter="onMouseEnter(item)"
      >
        <a
          v-if="item.open_new_tab"
          :href="item.url || '#'"
          class="nav-link"
          target="_blank"
          rel="noopener noreferrer"
        >{{ item.label }}</a>

        <!-- The label is itself the link even when a dropdown exists
             (walkthrough 01:39). -->
        <a
          v-else
          :href="item.url || '#'"
          class="nav-link"
          :class="{ 'nav-link--open': openId === item.id }"
          @click="onLabelClick($event, item)"
        >{{ item.label }}</a>
      </li>
    </ul>
    <button
      type="button"
      class="top-menu-scroll-arrow"
      aria-label="Scroll menu"
      @click="scrollTopMenu"
    >
      <span aria-hidden="true">&#8250;</span>
    </button>

    <!-- Panels mount on first hover and stay mounted; a few hundred nodes.
         Rendered at every viewport: the panel's inner uses max-width:1440px +
         justify-content:center, so the block stays centred at 1279 and below
         with the exact same style. -->
    <div v-if="openItem" class="top-menu__panel">
      <mega-menu-panel
        :item="openItem"
        :promo-grid="openPromoGrid"
        :promo-ratio="openPromoRatio"
        :promo-tile-layout="openPromoTileLayout"
        @navigate="closeMenu"
        @filter-search="onFilterSearch"
      />
    </div>
    <!-- Filter Search panel. Independent of the nav hover panel: it is opened by
         the FILTER SEARCH button and closed ONLY by its own Cancel/Apply buttons.
         Nothing auto-dismisses it — closeMenu (mouseleave) never touches
         filterOpen, and openMenu bails while it is open — so leaving the strip or
         hovering a nav item cannot close it (client: it must not vanish on
         focus-out, since it carries its own Cancel/Apply actions). -->
    <template v-if="filterOpen">
      <div class="top-menu__panel top-menu__panel--filter">
        <mega-menu-filter-panel
          @applied="filterOpen = false"
          @cancel="filterOpen = false"
        />
      </div>
    </template>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { followInternalLink } from '~/helpers/internal-link'
import MegaMenuPanel from './MegaMenuPanel.vue'
import MegaMenuFilterPanel from './MegaMenuFilterPanel.vue'
import topMenuScroll from '~/mixins/topMenuScroll'

/**
 * Hover-intent dwell: the cursor must rest on a nav item this long before its
 * panel opens, so a cursor merely crossing the strip never flashes a dropdown
 * open (client request). Once a panel is open, moving between top items switches
 * instantly — the dwell only guards the first open from a closed strip.
 */
const HOVER_OPEN_DELAY = 300

/**
 * The admin-configured public header navigation.
 *
 * Rendered by TopMenu.vue only when the `mega_menu_enabled` flag is on; with
 * the flag off this component is never mounted and the legacy markup runs
 * untouched.
 *
 * The payload is fetched once per session (guarded in the store), unlike the
 * legacy menu which refetches on every hover.
 */

export default {
  name: 'DynamicTopMenu',

  components: { MegaMenuPanel, MegaMenuFilterPanel },

  mixins: [topMenuScroll],

  data: () => ({
    openId: null,
    filterOpen: false,
    hoverTimer: null,
    // Modality of the pointerdown that precedes a click; `click` is not a
    // PointerEvent in every browser, so it cannot be read off the click itself.
    lastPointerType: 'mouse'
  }),

  computed: {
    ...mapGetters({
      items: 'mega-menu/megaMenuPublicItems'
    }),

    /**
     * The open panel is driven solely by openId (set on hover for pointer
     * devices, on tap for touch). No item hovered/tapped ⇒ no panel, so the
     * menu has a genuine closed state and can be dismissed.
     */
    openItem () {
      if (this.openId == null) return null

      const item = (this.items || []).find(row => row.id === this.openId)

      // A plain link (no dropdown) or an empty dropdown opens nothing.
      if (!item || !item.has_dropdown) return null

      return this.hasContent(item) ? item : null
    },

    openPromoGrid () {
      return this.openItem?.promo?.grid || [1, 1]
    },

    openPromoRatio () {
      return this.openItem?.promo?.ratio || '4:3'
    },

    openPromoTileLayout () {
      return this.openItem?.promo?.tile_layout || 'stacked'
    },

  },

  created () {
    this.fetchPublicMenu()
  },

  beforeDestroy () {
    this.clearHoverTimer()
  },

  methods: {
    ...mapActions({
      fetchPublicMenu: 'mega-menu/fetchPublicMenu'
    }),

    /** Does this item have anything worth showing in a panel? */
    hasContent (item) {
      const columns = item.columns || []
      const tiles = item.promo?.tiles || []

      return columns.length > 0 || tiles.length > 0
    },

    /**
     * Hover-to-open is decided by the pointer that actually fired the event.
     * `(hover: hover)` describes only the device's PRIMARY pointer, so a
     * touchscreen laptop reports `hover: none` and used to kill rollover for the
     * mouse plugged into it while clicking still worked (client report).
     * A tap also reaches here as pointerType 'touch' — onLabelClick owns tap.
     */
    onPointerEnter (event, item) {
      if (event.pointerType === 'touch') return

      this.openMenu(item)
    },

    /** Only browsers without Pointer Events get here; they have no touch/mouse split. */
    onMouseEnter (item) {
      if (window.PointerEvent) return

      this.openMenu(item)
    },

    openMenu (item) {
      // While the Filter Search panel is open it owns the strip: it closes only
      // via its own Cancel/Apply buttons, so hovering a nav item must neither
      // dismiss it nor open a nav dropdown over it.
      if (this.filterOpen) return

      this.clearHoverTimer()

      const openable = item.has_dropdown && this.hasContent(item)

      // A panel is already open: switch (or close on a plain link) instantly —
      // the dwell guard only governs the first open from a closed strip.
      if (this.openId != null) {
        this.openId = openable ? item.id : null
        return
      }

      if (!openable) return

      // Closed strip: require the cursor to rest for HOVER_OPEN_DELAY, so a
      // cursor crossing the strip cancels via mouseleave before it opens.
      this.hoverTimer = setTimeout(() => {
        this.hoverTimer = null
        this.openId = item.id
      }, HOVER_OPEN_DELAY)
    },

    clearHoverTimer () {
      if (this.hoverTimer) {
        clearTimeout(this.hoverTimer)
        this.hoverTimer = null
      }
    },

    closeMenu () {
      this.clearHoverTimer()
      this.openId = null
    },

    onLabelClick (event, item) {
      // Touch has no hover: tapping a nav item with a populated dropdown toggles
      // its panel instead of navigating; tapping it again (or another item)
      // closes/switches, giving touch users a way to dismiss the menu. A pen tap
      // counts as touch here (a stylus on a tablet must still reach the panel),
      // so only a mouse navigates. Keyed off the preceding pointerdown rather
      // than a device-wide media query, which would misread a hybrid.
      if (this.lastPointerType !== 'mouse' && item.has_dropdown && this.hasContent(item)) {
        event.preventDefault()
        this.filterOpen = false
        this.openId = this.openId === item.id ? null : item.id
        return
      }

      if (!item.url) {
        event.preventDefault()
        return
      }

      followInternalLink(event, this.$router)
      this.closeMenu()
    },

    onFilterSearch () {
      // Close the nav hover panel, then open the Filter Search panel. Kept
      // self-contained: no need to relay the event up through TopMenu/layout.
      this.openId = null
      this.filterOpen = true
    }
  }
}
</script>

<style scoped lang="scss">
/* Matches the legacy .top-menu chrome so the strip itself is unchanged. */
.top-menu {
  position: relative;
  background-color: #ffffff;
  border-bottom: 1px solid #e3e2e2;
}

.nav {
  /* More nav items than fit on one line wrap onto a second centred row rather
     than overflowing the viewport (walkthrough 09:21). The panel is anchored to
     the bottom of .top-menu, so it still opens under the whole strip however
     many rows it grows to. Declared here rather than relying on Bootstrap's
     .nav, so the behaviour survives a change of framework defaults. */
  margin: 0;
  flex-wrap: wrap;
}

.nav-item {
  position: static;
}

.nav-link {
  display: block;
  color: #898989;
  padding: 15px 0;
  margin: 0 22px;
  font-family: Arial, Helvetica, sans-serif;
  text-decoration: none;
  cursor: pointer;

  /* Tighten the horizontal spacing below desktop so the labels keep fitting
     (and wrap when they must) instead of the strip vanishing — mirrors the
     legacy header's tablet/mobile spacing. */
  @media (max-width: 1279px) {
    margin: 0 12px;
  }

  @media (max-width: 767px) {
    margin: 0 7px;
    padding: 12px 0;
  }

  &:hover,
  &--open {
    color: #383838;
    box-shadow: inset 0 -3px 0 0 #383838;
  }
}

.top-menu__panel {
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  z-index: 1030;
}

/* Transparent click-away that dismisses the Filter Search panel. Starts at the
   bottom of the strip (top: 100%) so the nav labels stay hoverable, and sits
   just below the panel (which keeps z-index 1030) so panel clicks never hit it. */
.top-menu__filter-backdrop {
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  height: 100vh;
  z-index: 1029;
  background: transparent;
}

.top-menu-scroll-arrow {
  display: none;
}

@media (min-width: 1280px) {
  .nav {
    justify-content: center;
  }
}

@media (max-width: 1279px) {
  .nav {
    flex-wrap: nowrap !important;
    overflow-x: auto;
    overflow-y: hidden;
    justify-content: flex-start !important;
    padding-left: 12px;
    padding-right: 52px;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .nav-item {
    flex-shrink: 0;
    white-space: nowrap;
  }

  .nav-link {
    white-space: nowrap;
  }
}

.top-menu--overflowing {
  .top-menu-scroll-arrow {
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 12px 2px 14px;
    border: 0;
    border-left: 2px solid #b8b8b8;
    color: #898989;
    font-size: 26px;
    font-weight: 300;
    line-height: 1;
    background: #ffffff;
    cursor: pointer;
    z-index: 2;
    transition: color 0.15s ease;

    &::before {
      content: '';
      position: absolute;
      right: 100%;
      top: 0;
      bottom: 0;
      width: 32px;
      background: linear-gradient(to right, rgba(255, 255, 255, 0), #ffffff 90%);
      pointer-events: none;
    }

    &:hover,
    &:focus-visible {
      color: #000;
    }

    &:focus-visible {
      outline: 2px solid #000;
      outline-offset: -2px;
    }
  }
}
</style>
